import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Building2, Mail, Users, Ticket, Palette, Check, ArrowRight, X, Rocket } from "lucide-react";
import { api } from "../lib/api";
import { useCompany } from "../context/CompanyContext";

export function OnboardingChecklist({ onStartTour }) {
  const navigate = useNavigate();
  const { managed } = useCompany();
  const [dismissed, setDismissed] = useState(() => localStorage.getItem("fd_onboard_dismissed") === "1");
  const [accounts, setAccounts] = useState([]);
  const [members, setMembers] = useState([]);
  const [tickets, setTickets] = useState([]);

  useEffect(() => {
    api.get("/email-accounts").then(({ data }) => setAccounts(data || [])).catch(() => {});
    api.get("/tickets").then(({ data }) => setTickets(data || [])).catch(() => {});
  }, []);
  useEffect(() => {
    const cid = managed[0]?.id;
    if (cid) api.get(`/companies/${cid}/members`).then(({ data }) => setMembers(data || [])).catch(() => {});
  }, [managed]);

  const brandCo = managed.find((c) => c.slug || c.logo_url);
  const steps = [
    { key: "company", icon: Building2, label: "Créer votre entreprise", desc: "Le socle de votre espace de travail.", done: managed.length > 0, cta: "Configurer", go: () => navigate("/companies") },
    { key: "email", icon: Mail, label: "Connecter une boîte email", desc: "Recevez et envoyez de vrais emails (IMAP/SMTP).", done: accounts.length > 0, cta: "Connecter", go: () => navigate("/mail") },
    { key: "member", icon: Users, label: "Inviter un collègue", desc: "Travaillez à plusieurs sur vos demandes.", done: members.length > 1, cta: "Inviter", go: () => navigate("/members") },
    { key: "ticket", icon: Ticket, label: "Créer votre premier ticket", desc: "Centralisez une demande client.", done: tickets.length > 0, cta: "Créer", go: () => navigate("/tickets") },
    { key: "portal", icon: Palette, label: "Personnaliser le portail client", desc: "Logo, couleurs et lien de marque.", done: !!brandCo, cta: "Personnaliser", go: () => navigate("/companies") },
  ];
  const doneCount = steps.filter((s) => s.done).length;
  const pct = Math.round((doneCount / steps.length) * 100);

  if (dismissed || doneCount === steps.length) return null;

  const dismiss = () => { localStorage.setItem("fd_onboard_dismissed", "1"); setDismissed(true); };

  return (
    <div data-testid="onboarding-checklist" className="rounded-2xl border border-klein/25 bg-gradient-to-br from-klein/[0.10] to-transparent p-5 mb-5 fd-rise">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex items-center gap-2.5">
          <div className="w-9 h-9 rounded-xl bg-klein/20 text-klein flex items-center justify-center"><Rocket size={18} /></div>
          <div>
            <h3 className="font-cabinet font-bold text-base leading-tight">Bien démarrer avec FlowDesk</h3>
            <p className="text-[11px] text-zinc-500">{doneCount}/{steps.length} étapes complétées</p>
          </div>
        </div>
        <div className="flex items-center gap-1.5">
          <button data-testid="onboarding-start-tour" onClick={onStartTour} className="hidden sm:inline-flex h-8 px-3 items-center gap-1.5 rounded-full bg-klein/15 text-klein text-xs font-semibold hover:bg-klein/25">Revoir la visite</button>
          <button data-testid="onboarding-dismiss" onClick={dismiss} title="Masquer" className="p-1.5 rounded-md text-zinc-400 hover:bg-white/10"><X size={16} /></button>
        </div>
      </div>
      <div className="h-1.5 rounded-full bg-white/10 mb-4 overflow-hidden">
        <div className="h-full bg-klein rounded-full transition-all duration-500" style={{ width: `${pct}%` }} />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        {steps.map((s) => {
          const Icon = s.icon;
          return (
            <div key={s.key} data-testid={`onboarding-step-${s.key}`} className={`flex items-center gap-3 rounded-xl border p-3 transition-colors ${s.done ? "border-emerald-500/25 bg-emerald-500/[0.06]" : "border-white/10 bg-background"}`}>
              <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${s.done ? "bg-emerald-500/20 text-emerald-400" : "bg-klein/15 text-klein"}`}>
                {s.done ? <Check size={16} /> : <Icon size={16} />}
              </div>
              <div className="min-w-0 flex-1">
                <div className={`text-sm font-medium truncate ${s.done ? "line-through text-zinc-500" : ""}`}>{s.label}</div>
                <div className="text-[11px] text-zinc-500 truncate">{s.desc}</div>
              </div>
              {!s.done && <button data-testid={`onboarding-cta-${s.key}`} onClick={s.go} className="shrink-0 flex items-center gap-1 h-7 px-2.5 rounded-lg bg-klein text-white text-xs font-semibold hover:brightness-110">{s.cta} <ArrowRight size={12} /></button>}
            </div>
          );
        })}
      </div>
    </div>
  );
}
